// SpotifyTrack type definition
export interface SpotifyTrack {
  id: string;
  name: string;
  artists: Array<{ name: string }>;
  album: {
    name: string;
    images: Array<{ url: string; width: number; height: number }>;
  };
  duration_ms: number;
  preview_url: string | null;
  explicit: boolean;
  popularity: number;
  external_urls: {
    spotify: string;
  };
}

class SpotifyService {
  private clientId: string;
  private clientSecret: string;
  private accessToken: string | null;
  private tokenExpiry: number | null;

  constructor() {
    this.clientId = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
    this.clientSecret = import.meta.env.VITE_SPOTIFY_CLIENT_SECRET;
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  async getAccessToken() {
    // Check if we have a valid token
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    try {
      console.log('Getting new Spotify access token...');
      
      const credentials = btoa(`${this.clientId}:${this.clientSecret}`);
      
      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'grant_type=client_credentials'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Spotify token error:', response.status, errorText);
        throw new Error(`Failed to get access token: ${response.status}`);
      }

      const data = await response.json();
      
      this.accessToken = data.access_token;
      // Set expiry to 90% of the actual expiry time for safety
      this.tokenExpiry = Date.now() + (data.expires_in * 1000 * 0.9);
      
      console.log('Got Spotify access token, expires in:', data.expires_in, 'seconds');
      return this.accessToken;
    } catch (error) {
      console.error('Error getting Spotify access token:', error);
      throw error;
    }
  }

  async searchTracks(query, limit = 10) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    try {
      const token = await this.getAccessToken();
      
      const searchUrl = new URL('https://api.spotify.com/v1/search');
      searchUrl.searchParams.append('q', query.trim());
      searchUrl.searchParams.append('type', 'track');
      searchUrl.searchParams.append('limit', limit.toString());

      const response = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Spotify search error:', response.status, errorText);
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return data.tracks.items.map(track => ({
        id: track.id,
        name: track.name,
        artists: track.artists || [],
        album: {
          name: track.album?.name || 'Unknown Album',
          images: track.album?.images || []
        },
        duration_ms: track.duration_ms,
        preview_url: track.preview_url,
        explicit: track.explicit,
        popularity: track.popularity,
        external_urls: track.external_urls
      }));
    } catch (error) {
      console.error('Error searching Spotify:', error);
      throw error;
    }
  }
}

const spotifyService = new SpotifyService();

// Named export for the hook
export { spotifyService };
// Default export for other uses
export default spotifyService; 